import { Link } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { ConsultationBand, PublicHero, PublicShell } from "@/components/PublicShell"
import { PublicMeta } from "@/components/public/PublicMeta"

const TITLE = "About Us: South African Wine Importer UK"
const DESCRIPTION =
  "SOH GD Wines is a premium South African wine importer for the UK: 50+ winemakers, labels by Lebone Paarl Labels and your brand on every bottle."

const PILLARS = [
  {
    kicker: "20",
    title: "Twenty years in corporate",
    body: "Duncan spent two decades in corporate life, most recently leading multi-million-pound recruitment solutions into the NHS. He knows what a well-run event asks of its host.",
  },
  {
    kicker: "50+",
    title: "South African winemakers",
    body: "We work with 50+ South African winemakers through our partnership with Sideways Wine, so the wine in the glass is one your guests genuinely want.",
  },
  {
    kicker: "2024",
    title: "Lebone Paarl Labels",
    body: "Every label is printed by Lebone Paarl Labels, South Africa's FINAT 2024 award-winning printer: a label that looks as though it belongs on the shelf.",
  },
]

export default function About() {
  return (
    <PublicShell>
      <PublicMeta title={TITLE} description={DESCRIPTION} />

      <PublicHero
        eyebrow="Our story"
        title={
          <>
            Part of the memory, <span className="soft">not just the host.</span>
          </>
        }
        lede="Everything is personalised now, except the one table everyone still gathers round. SOH GD Wines exists to change that."
      />

      <section className="band band-charcoal" aria-labelledby="why-title">
        <div className="shell split">
          <div>
            <div className="eyebrow">Why we exist</div>
            <h2 id="why-title">Every room was branded but the bottle</h2>
            <p className="lede">
              Your coffee cup, your feed, your trainers: everything carries a name now. Yet at the
              dinner, the away-day or the wedding, the one object in every hand still wears a
              stranger's label.
            </p>
            <p className="lede">
              Most small and mid-sized organisations never get to tell their story the way the big
              brands do. SOH GD Wines makes it simple: premium South African wine, with your brand on
              the bottle, from 100 bottles.
            </p>
            <p className="lede">
              Duncan was born in South Africa. The idea began around a braai on a trip home, grew
              arms and legs, and would not let go. Every order now quietly builds a UK market that
              supports the place he is from.
            </p>
          </div>
          {/* Placeholder imagery — replace after Duncan's photo shoot. */}
          <div className="split-media media-placeholder" role="img" aria-label="Photography of Duncan and the cellar to follow">
            <span className="media-placeholder-mark">
              SOH GD <em>Wines</em>
            </span>
            <span className="media-placeholder-note">Photography to follow</span>
          </div>
        </div>
      </section>

      <section className="band band-black" aria-labelledby="authority-title">
        <div className="shell">
          <div className="band-head">
            <div className="eyebrow">Who stands behind the bottle</div>
            <h2 id="authority-title">Done properly, with the right partners</h2>
            <p className="lede">
              When Duncan decided to do this, he decided to do it properly: the right wine, the right
              printer, and a personal service from the first conversation to the delivery.
            </p>
          </div>
          <div className="pillars">
            {PILLARS.map((pillar) => (
              <div key={pillar.title} className="pillar">
                <span className="pillar-kicker" aria-hidden="true">
                  {pillar.kicker}
                </span>
                <h3>{pillar.title}</h3>
                <p>{pillar.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band band-charcoal" aria-label="From the founder">
        <div className="shell pull">
          <div className="eyebrow eyebrow-center">From the founder</div>
          <blockquote>
            I'm building a world where the host isn't just the person who paid for the night, but
            part of the memory of it: where the bottle in every hand carries your name.
          </blockquote>
          <span className="pull-cite">Duncan Macdonald, Founder</span>
        </div>
      </section>

      <section className="band band-black" aria-labelledby="next-title">
        <div className="shell interlude">
          <div className="eyebrow eyebrow-center">Begin with the wine</div>
          <h2 id="next-title">See what we pour</h2>
          <p className="lede">
            Browse the range, then tell us about your occasion. If you would simply like the wine
            without a bespoke label, we can supply that too.
          </p>
          <Button asChild variant="ghost-hairline" size="lg">
            <Link href="/wines">View Our Wines</Link>
          </Button>
        </div>
      </section>

      <ConsultationBand brochure />
    </PublicShell>
  )
}
